import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class Commerce7Settings extends ModelObject<Commerce7Settings> {

    public version: number = 0;
    /**
    * Commerce7 tenant name. First part of the url when logged in.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public tenantName: string = "";
    private _outletId: Guid | null = null;
    /**
    * Outlet Id for all Commerce7 order imports.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    private _stockLocationId: Guid | null = null;
    /**
    * If provided then products on Commerce7 will be updated with the Stock Location's stock level in BrewMan.
    */
    public get stockLocationId(): Guid | null { return this._stockLocationId }
    public set stockLocationId(value: Guid | null) {
      this._stockLocationId = value === undefined ? null : value;
    }
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * If provided then Orders on Commerce7 from before this date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    private _defaultDeliveryStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for delivery charge of imported orders. This will be used if no BrewMan product name matches the Shipping Title or Code in Commerce7
    */
    public get defaultDeliveryStockItemId(): Guid | null { return this._defaultDeliveryStockItemId }
    public set defaultDeliveryStockItemId(value: Guid | null) {
      this._defaultDeliveryStockItemId = value === undefined ? null : value;
    }
    private _defaultTipStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for tips of imported orders.
    */
    public get defaultTipStockItemId(): Guid | null { return this._defaultTipStockItemId }
    public set defaultTipStockItemId(value: Guid | null) {
      this._defaultTipStockItemId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.tenantName = jsonSerializedObject.tenantName;
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.stockLocationId = jsonSerializedObject.stockLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.defaultDeliveryStockItemId = jsonSerializedObject.defaultDeliveryStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultDeliveryStockItemId);
          this.defaultTipStockItemId = jsonSerializedObject.defaultTipStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultTipStockItemId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.tenantName = this.tenantName;
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.stockLocationId = this.stockLocationId == null ? null : this.stockLocationId.toString();
        toRet.runInBackground = this.runInBackground;
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.defaultDeliveryStockItemId = this.defaultDeliveryStockItemId == null ? null : this.defaultDeliveryStockItemId.toString();
        toRet.defaultTipStockItemId = this.defaultTipStockItemId == null ? null : this.defaultTipStockItemId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "Commerce7Settings.OutletId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => v == null || !v.isEmpty() || "Commerce7Settings.StockLocationId cannot be empty"},
            {field: "defaultDeliveryStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "Commerce7Settings.DefaultDeliveryStockItemId cannot be empty"},
            {field: "defaultTipStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "Commerce7Settings.DefaultTipStockItemId cannot be empty"},
    ];
}


export class Commerce7SettingsService extends ServiceBase {

  public async getCommerce7Settings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Commerce7Settings> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new Commerce7Settings(response.commerce7Settings);;
    return this.makeJsonRequest("commerce7Settings/v1/getCommerce7Settings", req, false, extractor, opts);
  };

  public async saveCommerce7Settings(commerce7Settings: Commerce7Settings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    commerce7Settings:commerce7Settings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("commerce7Settings/v1/saveCommerce7Settings", req, true, extractor, opts);
  };

  public async hardDeleteCommerce7Settings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("commerce7Settings/v1/hardDeleteCommerce7Settings", req, true, extractor, opts);
  };

  public async testCommerce7Connection(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<boolean> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.isConnected;;
    return this.makeJsonRequest("commerce7Settings/v1/testCommerce7Connection", req, false, extractor, opts);
  };
}

export const commerce7SettingsService = new Commerce7SettingsService();